"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

type Language = 'en' | 'hi' | 'es' | 'fr' | 'ar' | 'de' | 'pt' | 'ru' | 'ja' | 'zh' | 'it' | 'ko';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
    formatDate: (date: Date | string) => string;
}

// Initial English dictionary to prevent flash of untranslated content
import { en } from '@/dictionaries/en';
const fallbackDict: Record<string, string> = en;

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguageState] = useState<Language>('en');
    const [dictionary, setDictionary] = useState<Record<string, string>>(fallbackDict);

    // Sync HTML attributes
    const syncHtml = (lang: Language) => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    };

    // Load language dictionary dynamically for performance (Bundle Splitting)
    const loadDictionary = async (lang: Language) => {
        if (lang === 'en') {
            setDictionary(en);
            return;
        }

        try {
            // Dynamic import creates separate chunks for each language
            // This reduces unused JS by several MBs
            let module;
            switch (lang) {
                case 'hi': module = await import('@/dictionaries/hi'); break;
                case 'es': module = await import('@/dictionaries/es'); break;
                case 'fr': module = await import('@/dictionaries/fr'); break;
                case 'ar': module = await import('@/dictionaries/ar'); break;
                case 'de': module = await import('@/dictionaries/de'); break;
                case 'pt': module = await import('@/dictionaries/pt'); break;
                case 'ru': module = await import('@/dictionaries/ru'); break;
                case 'ja': module = await import('@/dictionaries/ja'); break;
                case 'zh': module = await import('@/dictionaries/zh'); break;
                case 'it': module = await import('@/dictionaries/it'); break;
                case 'ko': module = await import('@/dictionaries/ko'); break;
                default: module = { [lang]: en };
            }
            // @ts-ignore
            setDictionary(module[lang]);
        } catch (error) {
            console.error(`Failed to load dictionary for ${lang}`, error);
            setDictionary(en);
        }
    };

    useEffect(() => {
        const savedLanguage = localStorage.getItem('language') as Language;
        if (savedLanguage && savedLanguage !== 'en') {
            setLanguageState(savedLanguage);
            loadDictionary(savedLanguage);
            syncHtml(savedLanguage);
        } else {
            syncHtml('en');
        }
    }, []);

    const setLanguage = useCallback(async (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('language', lang);
        syncHtml(lang);
        await loadDictionary(lang);
    }, []);

    const t = useCallback((key: string): string => {
        return dictionary[key] || fallbackDict[key] || key;
    }, [dictionary]);

    const formatNumber = useCallback((num: number, options?: Intl.NumberFormatOptions): string => {
        const locale = language === 'hi' ? 'en-IN' : language;
        return new Intl.NumberFormat(locale, options).format(num);
    }, [language]);

    const formatDate = useCallback((date: Date | string): string => {
        const d = typeof date === 'string' ? new Date(date) : date;
        const locale = language === 'hi' ? 'en-IN' : language;
        return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(d);
    }, [language]);

    const value = useMemo(() => ({ language, setLanguage, t, formatNumber, formatDate }), [language, setLanguage, t, formatNumber, formatDate]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};




